import { Link, useLocation } from "react-router-dom";
import logo from "../assets/images/avatar1.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDown,
  faAngleRight,
  faBars,
  faCaretDown,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState, useRef } from "react";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import { motion, AnimatePresence } from "framer-motion";
import MobileHeader from "./mobileHeader.component";
import { useUserParams } from "../hooks/useUserParams";

export default function Header() {
  const { getCurrentLogo } = useUserParams();
  const [menus, setMenus] = useState({
    buying: false,
    selling: false,
    info: false,
    locations: false,
  });
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [headerPosition, setHeaderPosition] = useState(100);
  const [headerTiming, setHeaderTiming] = useState(0.8);
  const location = useLocation();
  const [path, setPath] = useState(location.pathname);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {

    const onScroll = () => {

      setScrolled(window.scrollY > 50);

    };

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);

  }, []);


  // Refs
  const headerRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };

  const menuVariants = {
    hidden: {
      opacity: 0,
      y: -8,
    },
    visible: {
      opacity: 1,
      y: 0,
    },
    transition: {
      duration: 0.35,
      ease: "easeOut",
    }
  };

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  const setDropdown = (key, bool) => {
    setMenus((prevMenus) => ({
      ...prevMenus,
      ...Object.fromEntries(
        Object.keys(prevMenus).map((k) => [k, k === key ? bool : false])
      ),
    }));
  };

  const navClick = () => {
    setHeaderPosition(100);
    setHeaderTiming(0);
    setTimeout(() => {
      setHeaderTiming(0.8);
    }, 100);
    setMenus({ buying: false, selling: false, info: false, locations: false });
  };

  useEffect(() => {
    setPath(location.pathname);
  }, [location]);

  const adjustHeader = () => {
    const st = window.scrollY;
    const header = headerRef.current;

    if (!header) return;

    if (st !== 0) {
      header.classList.add("transparent");
    } else if (location.pathname === "/") {
      header.classList.remove("transparent");
    }

    if (st > lastScrollTopRef.current) {
      header.classList.add("hide-header");
    } else if (st < lastScrollTopRef.current) {
      header.classList.remove("hide-header");
    }

    lastScrollTopRef.current = st <= 0 ? 0 : st;
  };

  useEffect(() => {
    window.addEventListener("scroll", adjustHeader);
    return () => {
      window.removeEventListener("scroll", adjustHeader);
    };
  }, [location, path]);

  return (
    <>
      {windowSize > 1250 ? (
        <div ref={headerRef}
          className="fixed top-6 z-[100] w-full px-16 duration-500"
        >
          <nav
            className={`flex items-center justify-between rounded-3xl duration-300 border border-white/60 bg-harbour-navy backdrop-blur-2xl h-20 px-8  
              ${scrolled
                ? "shadow-xl border-2"
                : "shadow-[0_15px_50px_rgba(15,23,42,.08)]"
              }`}
          >
            {/* Logo */}
            <Link to="/" className="shrink-0">
              <img
                src={getCurrentLogo()}
                className="h-10"
                alt="Logo"
              />
            </Link>

            {/* Navigation */}
            <div className="hidden xl:flex items-center gap-10">
              {constants.NAVIGATION.map((item) => {
                if (!item.children) {
                  return (
                    <Link
                      key={item.title}
                      to={item.to}
                      className="relative text-[14px] font-medium tracking-[0.18em] uppercase text-harbour-sky transition after:absolute after:left-0 after:-bottom-2 after:h-[2px] after:w-0 after:bg-harbour-accent after:duration-300 hover:after:w-full"
                    >
                      {item.title}
                    </Link>
                  );
                }

                return (
                  <div
                    key={item.slug}
                    className="relative"
                    onMouseEnter={() => setActiveMenu(item.slug)}
                    onMouseLeave={() => setActiveMenu(null)}
                  >
                    <button
                      className="flex items-center gap-2 uppercase tracking-[0.18em] text-[14px] text-harbour-sky"
                    >
                      {item.title}
                      <FontAwesomeIcon
                        icon={faAngleDown}
                        className={`duration-300 ${activeMenu === item.slug
                          ? "rotate-180"
                          : ""
                          }`}
                      />
                    </button>

                    <AnimatePresence>
                      {activeMenu === item.slug && (
                        <motion.div
                          initial={{
                            opacity: 0,
                            x: "-50%",
                            y: -25,
                          }}
                          animate={{
                            opacity: 1,
                            x: "-50%",
                            y: 0,
                          }}
                          exit={{
                            opacity: 0,
                            x: "-50%",
                            y: -15,
                          }}
                          transition={{
                            duration: 0.35,
                          }}
                          className="absolute top-full left-1/2 mt-6 w-64 rounded-3xl bg-white p-3 shadow-2xl"
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.to}
                              to={child.to}
                              className="block rounded-2xl px-5 py-3 text-center text-harbour-navy hover:bg-harbour-sky"
                            >
                              {child.title}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              className="rounded-full bg-harbour-blue px-6 py-3.5 text-sm uppercase tracking-[0.18em] text-white transition hover:bg-harbour-blue/80"
            >
              Contact
            </Link>
          </nav>
        </div>
      ) : (
        <MobileHeader location={location} path={path} adjustHeader={adjustHeader} headerRef={headerRef} />
      )}
    </>
  );
}
